import type {
  ConfidenceLevelApi,
  DateFormatApi,
  ForecastHorizonApi,
  NumberFormatApi,
  ThemeApi,
} from "../api/models/userPreferenceDefaults";

export interface PreferenceOption<TValue extends string = string> {
  value: TValue;
  label: string;
  description?: string;
}

export interface UserPreferenceDefaultsUi {
  dateFormat: DateFormatApi;
  numberFormat: NumberFormatApi;
  forecastHorizon: ForecastHorizonApi;
  lowCashBalanceThreshold: number;
  overdueInvoiceAlert: boolean;
  varianceAlertThreshold: number;
  emailNotifications: boolean;
  feedNotifications: boolean;
  confidenceLevel: ConfidenceLevelApi;
  includeWeekends: boolean;
  theme: ThemeApi;
  dateFormatOptions: PreferenceOption<DateFormatApi>[];
  numberFormatOptions: PreferenceOption<NumberFormatApi>[];
  forecastHorizonOptions: PreferenceOption<ForecastHorizonApi>[];
  confidenceLevelOptions: PreferenceOption<ConfidenceLevelApi>[];
  themeOptions: PreferenceOption<ThemeApi>[];
}

export const DATE_FORMAT_OPTIONS: PreferenceOption<DateFormatApi>[] = [
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY", description: "e.g., 17/01/2025" },
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY", description: "e.g., 01/17/2025" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD", description: "e.g., 2025-01-17" },
];

export const NUMBER_FORMAT_OPTIONS: PreferenceOption<NumberFormatApi>[] = [
  {
    value: "comma-dot",
    label: "£1,000.00",
    description: "comma separator, dot decimal",
  },
  {
    value: "dot-comma",
    label: "£1.000,00",
    description: "dot separator, comma decimal",
  },
  { value: "none-dot", label: "£1000.00", description: "no separator" },
];

export const FORECAST_HORIZON_OPTIONS: PreferenceOption<ForecastHorizonApi>[] =
  [
    { value: "7-days", label: "7 Days", description: "short-term view" },
    { value: "30-days", label: "30 Days", description: "weekly view" },
    { value: "90-days", label: "90 Days", description: "recommended" },
  ];

export const CONFIDENCE_LEVEL_OPTIONS: PreferenceOption<ConfidenceLevelApi>[] =
  [
    { value: "low", label: "Low (70%)" },
    { value: "medium", label: "Medium (90%)" },
    { value: "high", label: "High (95%)" },
  ];

export const THEME_OPTIONS: PreferenceOption<ThemeApi>[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

export const DEFAULT_USER_PREFERENCE_DEFAULTS: Omit<
  UserPreferenceDefaultsUi,
  | "dateFormatOptions"
  | "numberFormatOptions"
  | "forecastHorizonOptions"
  | "confidenceLevelOptions"
  | "themeOptions"
> = {
  dateFormat: "DD/MM/YYYY",
  numberFormat: "comma-dot",
  forecastHorizon: "90-days" as ForecastHorizonApi,
  lowCashBalanceThreshold: 50000,
  overdueInvoiceAlert: true,
  varianceAlertThreshold: 10,
  emailNotifications: true,
  feedNotifications: true,
  confidenceLevel: "medium",
  includeWeekends: false,
  theme: "light",
};
